import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Sparkles, Hand, Cpu, Wifi, MessageCircle, Phone,
  Check, Apple, Quote,
} from "lucide-react";
import * as Icons from "lucide-react";
import { useSEO } from "../lib/seo";
import { useCmsSeo } from "../lib/cmsSeo";
import { usePageCms } from "../cms/usePageCms";
import { BRAND } from "../data/content";
import { DYNAMIC_SERIES_GALLERY } from "../data/dynamicSeriesGallery";
import {
  DS_HERO_DEVICES,
  DS_MODULES,
  DS_FINISHES,
  DS_PILLARS,
  DS_PHILOSOPHY,
  DS_HERO_CLOSEUP,
} from "../data/dynamicSeriesContent";

function I({ name, className }) {
  const Cmp = Icons[name] || Sparkles;
  return <Cmp className={className} />;
}

export default function DynamicSeriesPage({ onBook }) {
  const seo = useCmsSeo("dynamic-series", {
    title: "Dynamic Series — The switch, reimagined",
    description:
      "GenieX Dynamic Series — a sculpted smart switch with an always-on display, tactile keys, seven curated finishes and Zigbee 3.0 mesh. Inspired by Apple. Crafted in India.",
    path: "/dynamic-series",
    keywords:
      "Dynamic Series, modular smart switch, 4M 6M 8M 12M smart switch India, luxury smart switch, Zigbee 3.0, GenieX",
  });
  useSEO(seo);

  const waLink = `https://wa.me/${BRAND.phoneRaw.replace("+", "")}?text=${encodeURIComponent(
    "Hi GenieX, I'd like a private demo of the Dynamic Series."
  )}`;
  const { data: pageCms } = usePageCms("dynamic-series");
  const heroCms = pageCms.hero || {};

  return (
    <main data-testid="dynamic-series-page" className="bg-[var(--gx-bg)] text-white overflow-x-hidden">
      <Hero onBook={onBook} waLink={waLink} cms={heroCms} />
      <Marquee />
      <Sculpted />
      <Philosophy />
      <Pillars />
      <SmartBeautiful />
      <Modules />
      <LuxuryOnWall />
      <Finishes />
      <LiveUI onBook={onBook} />
      <Gallery />
      <CTA onBook={onBook} waLink={waLink} />
    </main>
  );
}

/* ---------- HERO ---------- */
function Hero({ onBook, waLink, cms = {} }) {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((v) => (v + 1) % DS_HERO_DEVICES.length), 3800);
    return () => clearInterval(t);
  }, []);
  const current = DS_HERO_DEVICES[idx];
  if (cms.visible === false) return null;

  return (
    <section className="relative min-h-[100svh] flex items-center overflow-hidden grain">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(201,169,97,0.22),transparent_55%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_85%_85%,rgba(255,255,255,0.04),transparent_60%)]" />
      <div className="absolute inset-0 vignette" />

      <div className="relative max-w-[1400px] mx-auto px-5 lg:px-10 pt-32 lg:pt-40 pb-20 grid lg:grid-cols-2 gap-12 items-center w-full">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-gold/30 bg-gold/10 text-[11px] tracking-[0.18em] uppercase text-amber-100">
            <Sparkles className="w-3 h-3" /> {cms.eyebrow || "The Flagship"}
          </div>
          <h1 className="font-display tracking-tight text-white mt-7 text-4xl sm:text-5xl lg:text-[76px] leading-[0.98] max-w-[14ch]">
            {cms.heading_line1 || "The future of"} <br />
            <span className="text-gold">{cms.heading_accent || "wall control."}</span>
          </h1>
          <p className="lead mt-7 max-w-[46ch]">
            {cms.description ||
              "A single, sculpted surface. Tactile keys, an informative display, and the entire smart home — discreetly woven into one premium switch."}
          </p>
          <div className="flex flex-wrap gap-3 mt-10">
            {cms.primary_cta_link ? (
              <a href={cms.primary_cta_link} data-testid="ds-page-book" className="btn-gold">
                {cms.primary_cta_label || "Book a Live Demo"} <ArrowRight className="w-4 h-4" />
              </a>
            ) : (
              <button onClick={onBook} data-testid="ds-page-book" className="btn-gold">
                {cms.primary_cta_label || "Book a Live Demo"} <ArrowRight className="w-4 h-4" />
              </button>
            )}
            <a
              href={cms.secondary_cta_link || waLink}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="ds-page-whatsapp"
              className="btn-outline"
            >
              <MessageCircle className="w-4 h-4" /> {cms.secondary_cta_label || "WhatsApp Us"}
            </a>
          </div>
          <div className="flex items-center gap-4 mt-6 text-[12px] text-mute">
            <a
              href={`tel:${BRAND.phoneRaw}`}
              className="hover:text-gold transition-colors inline-flex items-center gap-2"
            >
              <Phone className="w-3.5 h-3.5" /> {BRAND.phone}
            </a>
            <span>·</span>
            <span>5-year warranty in writing</span>
          </div>
        </div>

        {/* Animated device showcase */}
        <div className="relative">
          <div className="absolute -inset-10 rounded-[60px] bg-[radial-gradient(circle,rgba(201,169,97,0.24),transparent_65%)] blur-2xl" />
          <div className="relative aspect-square w-full max-w-[560px] mx-auto">
            {DS_HERO_DEVICES.map((d, i) => (
              <img
                key={d.src}
                src={d.src}
                alt={d.label}
                className={`absolute inset-0 w-full h-full object-contain transition-opacity duration-1000 drop-shadow-[0_40px_80px_rgba(0,0,0,0.7)] ${
                  i === idx ? "opacity-100" : "opacity-0"
                }`}
                data-testid={`hero-device-${i}`}
              />
            ))}
            {/* Label chip — modular size, not button count */}
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 inline-flex flex-col items-center gap-0.5 px-5 py-2 rounded-2xl border border-line bg-black/55 backdrop-blur-md">
              <span className="text-[12px] tracking-[0.32em] uppercase text-gold font-semibold">
                {current.label}
              </span>
              {current.note && (
                <span className="text-[10.5px] tracking-[0.18em] uppercase text-amber-100/70">
                  {current.note}
                </span>
              )}
            </div>
            {/* Dot indicators */}
            <div className="absolute -bottom-12 left-1/2 -translate-x-1/2 flex items-center gap-2">
              {DS_HERO_DEVICES.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setIdx(i)}
                  aria-label={`Show device ${i + 1}`}
                  data-testid={`hero-dot-${i}`}
                  className={`h-1.5 rounded-full transition-all ${
                    i === idx ? "bg-gold w-8" : "bg-white/20 w-1.5 hover:bg-white/40"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- MARQUEE ---------- */
function Marquee() {
  const line =
    "GENIEX · LAUNCHING A NEW ERA OF HOME AUTOMATION · DYNAMIC SERIES · ";
  return (
    <section className="relative border-y border-line/60 bg-[var(--gx-black)] py-6 overflow-hidden">
      <div className="flex whitespace-nowrap animate-[marquee_28s_linear_infinite] gap-12 text-[11px] tracking-[0.32em] text-amber-100/70 uppercase">
        {Array.from({ length: 6 }).map((_, i) => (
          <span key={i} className="inline-flex items-center gap-12">
            <span>{line}</span>
            <Sparkles className="w-3 h-3 text-gold" />
          </span>
        ))}
      </div>
      <style>{`
        @keyframes marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }
      `}</style>
    </section>
  );
}

/* ---------- SCULPTED ---------- */
function Sculpted() {
  return (
    <section className="border-t border-line/60">
      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-20 lg:py-28 grid lg:grid-cols-2 gap-14 items-center">
        <div>
          <p className="eyebrow mb-4">The Philosophy</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.04] max-w-[16ch]">
            Sculpted to <span className="text-gold">disappear.</span><br />
            Engineered to be <span className="text-gold">felt.</span>
          </h2>
          <p className="lead mt-7 max-w-[54ch]">
            Every line of the Dynamic Series is drawn for one purpose — to put the
            entire intelligence of your home under your fingertips, without ever
            asking for attention.
          </p>
        </div>
        <div className="relative">
          <div className="absolute -inset-8 rounded-[40px] bg-[radial-gradient(circle,rgba(201,169,97,0.18),transparent_60%)] blur-2xl" />
          <div className="relative rounded-3xl overflow-hidden border border-line">
            <img
              src={DS_HERO_CLOSEUP}
              alt="Dynamic Series close-up on a textured wall"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
            <div className="absolute bottom-5 left-5 text-[10px] tracking-[0.32em] uppercase text-amber-100 bg-black/40 backdrop-blur-md px-3 py-1.5 rounded-full border border-line">
              Lifestyle · 6M Classic
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- PHILOSOPHY (Apple-inspired / Steve Jobs lineage) ---------- */
function Philosophy() {
  return (
    <section
      className="relative border-t border-line/60 overflow-hidden bg-[var(--gx-black)]"
      data-testid="ds-philosophy"
    >
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_10%,rgba(201,169,97,0.18),transparent_55%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_90%,rgba(255,255,255,0.04),transparent_50%)]" />
      <div className="relative max-w-[1400px] mx-auto px-5 lg:px-10 py-24 lg:py-32">
        {/* Header band */}
        <div className="flex flex-wrap items-end justify-between gap-8 mb-16">
          <div className="max-w-[36ch]">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-gold/30 bg-gold/10 text-[11px] tracking-[0.18em] uppercase text-amber-100">
              <Apple className="w-3.5 h-3.5" /> {DS_PHILOSOPHY.eyebrow}
            </div>
            <h2
              className="font-display tracking-tight text-white mt-6 text-3xl sm:text-4xl lg:text-[58px] leading-[1.04]"
              dangerouslySetInnerHTML={{
                __html: DS_PHILOSOPHY.title.replace(
                  "Steve\u00a0Jobs",
                  "<span class='text-gold italic'>Steve&nbsp;Jobs</span>"
                ),
              }}
            />
          </div>
          <p className="lead max-w-[46ch]">{DS_PHILOSOPHY.body}</p>
        </div>

        {/* Steve Jobs pull-quote — large, centred, theatrical */}
        <figure className="relative mx-auto max-w-[58ch] text-center my-20">
          <Quote
            aria-hidden
            className="absolute -top-10 left-1/2 -translate-x-1/2 w-14 h-14 text-gold/30"
          />
          <blockquote className="font-display text-2xl sm:text-3xl lg:text-[40px] leading-[1.18] italic text-white/95">
            &ldquo;{DS_PHILOSOPHY.quote}&rdquo;
          </blockquote>
          <figcaption className="mt-8 flex items-center justify-center gap-3 text-[11px] tracking-[0.32em] uppercase text-amber-100/80">
            <span className="block w-12 h-px bg-gold/60" />
            {DS_PHILOSOPHY.quoteBy}
            <span className="block w-12 h-px bg-gold/60" />
          </figcaption>
        </figure>

        {/* Three "beats" — declarative cards in Apple keynote rhythm */}
        <div className="grid md:grid-cols-3 gap-5">
          {DS_PHILOSOPHY.beats.map((b, i) => (
            <article
              key={b.title}
              className="relative rounded-2xl border border-line bg-[var(--gx-surface)]/60 backdrop-blur-md p-7 hover:border-gold/40 hover:-translate-y-1 transition-all duration-300"
              data-testid={`ds-philosophy-beat-${i}`}
            >
              <div className="font-display text-gold text-[42px] leading-none mb-4 opacity-70">
                {String(i + 1).padStart(2, "0")}
              </div>
              <h3 className="font-display text-xl tracking-tight text-white">
                {b.title}
              </h3>
              <p className="text-dim text-[13.5px] mt-3 leading-relaxed">
                {b.body}
              </p>
            </article>
          ))}
        </div>

        {/* Footer line — we follow proudly */}
        <p className="text-center text-[11px] tracking-[0.32em] uppercase text-amber-100/70 mt-16">
          We follow these steps — proudly.
        </p>
      </div>
    </section>
  );
}

/* ---------- PILLARS (4 features) ---------- */
function Pillars() {
  return (
    <section className="border-t border-line/60 bg-gradient-to-b from-[var(--gx-bg)] via-[#0B0B10] to-[var(--gx-bg)]">
      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-20 lg:py-28">
        <div className="max-w-[60ch]">
          <p className="eyebrow mb-4">Engineering</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.08]">
            Four pillars. <span className="text-gold">One surface.</span>
          </h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mt-14">
          {DS_PILLARS.map((p) => (
            <div
              key={p.title}
              className="group relative rounded-2xl border border-line bg-[var(--gx-surface)]/60 backdrop-blur-md p-7 hover:border-gold/40 hover:-translate-y-1 transition-all duration-300"
              data-testid={`pillar-${slugify(p.title)}`}
            >
              <div className="w-11 h-11 rounded-xl border border-gold/30 flex items-center justify-center text-gold mb-5 group-hover:border-gold/60 transition-colors">
                <I name={p.icon} className="w-5 h-5" />
              </div>
              <h3 className="font-display text-xl tracking-tight">{p.title}</h3>
              <p className="text-dim text-[13.5px] mt-3 leading-relaxed">{p.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- SMART INSIDE / BEAUTIFUL OUTSIDE ---------- */
function SmartBeautiful() {
  return (
    <section className="border-t border-line/60">
      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-20 lg:py-28">
        <div className="text-center max-w-[24ch] mx-auto">
          <p className="eyebrow mb-4">Inside · Outside</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-6xl tracking-tight leading-[1.04]">
            Smart inside. <span className="italic text-gold">Beautiful</span> outside.
          </h2>
        </div>
        <p className="lead mt-7 mx-auto max-w-[58ch] text-center">
          The Dynamic Series fits the modular boxes you already have, replaces the
          switches you already own, and quietly upgrades every wall.
        </p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-14 max-w-[1000px] mx-auto">
          {[
            "Fits standard Indian modular boxes — 4M / 6M / 8M / 12M.",
            "No new wiring. Installs in under 20 minutes per switch.",
            "Works on Zigbee 3.0 — independent of your home Wi-Fi.",
            "Five years of warranty. Same-week service across South India.",
          ].map((t) => (
            <div key={t} className="flex items-start gap-3 rounded-2xl border border-line p-5 bg-[var(--gx-surface)]/40">
              <Check className="w-4 h-4 text-gold mt-0.5 shrink-0" />
              <p className="text-[13.5px] text-white/90 leading-relaxed">{t}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- MODULES ---------- */
function Modules() {
  return (
    <section className="border-t border-line/60 bg-[var(--gx-black)]">
      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-20 lg:py-28">
        <div className="max-w-[60ch]">
          <p className="eyebrow mb-4">Form Factors</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.08]">
            Four modular sizes. <span className="text-gold">One language.</span>
          </h2>
          <p className="lead mt-7 max-w-[58ch]">
            Every Dynamic fits a standard Indian modular box — 4M, 6M, 8M or 12M.
            Mix, match and upgrade one wall at a time, without re-doing your wiring.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-14">
          {DS_MODULES.map((m) => (
            <div
              key={m.name}
              className="group relative rounded-2xl overflow-hidden border border-line bg-[var(--gx-surface)]/60 backdrop-blur-md hover:border-gold/40 transition-colors"
              data-testid={`module-${slugify(m.name)}`}
            >
              <div className="relative aspect-[3/4] overflow-hidden bg-black">
                <img
                  src={m.img}
                  alt={m.name}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-transparent to-transparent" />
                <div className="absolute bottom-4 left-4 right-4">
                  <div className="text-[10px] tracking-[0.32em] uppercase text-amber-100/80 mb-1">
                    {m.spec}
                  </div>
                  <div className="font-display text-xl text-white">{m.name}</div>
                </div>
              </div>
              <div className="p-5">
                <p className="text-dim text-[12.5px] leading-relaxed">{m.blurb}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- LUXURY ON YOUR WALL ---------- */
function LuxuryOnWall() {
  return (
    <section className="border-t border-line/60 relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_-20%,rgba(201,169,97,0.20),transparent_55%)]" />
      <div className="relative max-w-[1400px] mx-auto px-5 lg:px-10 py-24 lg:py-32 text-center">
        <p className="eyebrow mb-4">A Single Language</p>
        <h2 className="font-display text-4xl sm:text-5xl lg:text-7xl tracking-tight leading-[1.0] max-w-[18ch] mx-auto">
          Luxury on your <span className="italic text-gold">wall.</span>
        </h2>
        <p className="text-mute text-[13px] tracking-[0.32em] uppercase mt-10">
          One switch · Everything in place
        </p>
      </div>
    </section>
  );
}

/* ---------- FINISHES ---------- */
function Finishes() {
  const [active, setActive] = useState(DS_FINISHES[0]);
  return (
    <section className="border-t border-line/60 bg-[var(--gx-black)]">
      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-20 lg:py-28 grid lg:grid-cols-[1fr_1.2fr] gap-14 items-center">
        <div>
          <p className="eyebrow mb-4">Seven Finishes</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.04]">
            Pick a finish. <span className="text-gold">Set the tone.</span>
          </h2>
          <p className="lead mt-7 max-w-[52ch]">
            Each finish is hand-applied across glass, metal and polymer — perfectly
            matched, top to bottom, switch to socket.
          </p>

          <div className="mt-12 grid grid-cols-4 gap-3 max-w-[420px]">
            {DS_FINISHES.map((f) => (
              <button
                key={f.name}
                onClick={() => setActive(f)}
                onMouseEnter={() => setActive(f)}
                data-testid={`finish-${slugify(f.name)}`}
                aria-label={f.name}
                className={`relative aspect-square rounded-2xl border transition-all ${
                  active.name === f.name
                    ? "border-gold scale-105 ring-2 ring-gold/30"
                    : "border-line hover:border-gold/50"
                }`}
                style={{ background: f.hex }}
              >
                {active.name === f.name && (
                  <Check className="absolute inset-0 m-auto w-4 h-4 text-white drop-shadow-md" />
                )}
              </button>
            ))}
          </div>
          <p className="text-[12px] tracking-[0.18em] uppercase text-amber-100/80 mt-6" data-testid="finish-active-label">
            {active.name}
          </p>
        </div>

        <div className="relative">
          <div className="absolute -inset-10 rounded-[60px] bg-[radial-gradient(circle,rgba(201,169,97,0.18),transparent_65%)] blur-2xl" />
          <div className="relative rounded-3xl overflow-hidden border border-line aspect-square max-w-[520px] mx-auto">
            <img
              key={active.img}
              src={active.img}
              alt={active.name}
              className="w-full h-full object-cover animate-[fadeIn_0.6s_ease-out]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
          </div>
          <style>{`
            @keyframes fadeIn { from { opacity: 0; transform: scale(1.02); } to { opacity: 1; transform: scale(1); } }
          `}</style>
        </div>
      </div>
    </section>
  );
}

/* ---------- LIVE UI ---------- */
function LiveUI({ onBook }) {
  return (
    <section className="border-t border-line/60">
      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-20 lg:py-28 grid lg:grid-cols-2 gap-14 items-center">
        <div className="order-2 lg:order-1 relative">
          <div className="absolute -inset-8 rounded-[40px] bg-[radial-gradient(circle,rgba(201,169,97,0.18),transparent_60%)] blur-2xl" />
          <div className="relative rounded-3xl border border-line bg-gradient-to-br from-white/[0.05] to-white/[0.01] backdrop-blur-xl p-8 aspect-square max-w-[520px] mx-auto overflow-hidden">
            <div className="absolute inset-0 opacity-30" style={{ backgroundImage: "linear-gradient(rgba(255,255,255,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.06) 1px, transparent 1px)", backgroundSize: "32px 32px" }} />
            <UIDeviceMockup />
          </div>
        </div>

        <div className="order-1 lg:order-2">
          <p className="eyebrow mb-4">Live UI</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.04]">
            Try the Dynamic UI <br />
            <span className="italic text-gold">before</span> you buy the wall.
          </h2>
          <p className="lead mt-7 max-w-[52ch]">
            The full interactive switch interface — display, keys, scenes — running in
            your browser. Tap, swipe, configure. Feel exactly how it works on a real
            wall.
          </p>
          <div className="flex flex-wrap items-center gap-3 mt-10">
            <button onClick={onBook} data-testid="liveui-cta-book" className="btn-gold">
              Book a Live Demo <ArrowRight className="w-4 h-4" />
            </button>
            <Link to="/solutions/complete-home" className="btn-outline">
              Explore Complete Home
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function UIDeviceMockup() {
  return (
    <div className="relative w-full h-full flex flex-col items-center justify-center">
      {/* Display */}
      <div className="w-full aspect-[2/1] rounded-xl border border-gold/30 bg-black/40 backdrop-blur-md mb-6 p-6 flex items-center justify-between">
        <div>
          <div className="font-display text-3xl text-white">8:42<span className="text-gold">.</span></div>
          <div className="text-[11px] tracking-[0.28em] text-amber-200/80 uppercase mt-1">Wed · 27 °C</div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="flex items-center gap-1.5 text-[11px] tracking-[0.18em] text-amber-100/80 uppercase">
            <Wifi className="w-3 h-3" /> Mesh
          </div>
          <div className="text-[11px] tracking-[0.18em] text-mute uppercase">Living · Scene</div>
        </div>
      </div>
      {/* Keys */}
      <div className="grid grid-cols-3 gap-4 w-full">
        {["Relax", "Movie", "Dinner", "Read", "Goodnight", "Away"].map((k) => (
          <div
            key={k}
            className="aspect-square rounded-xl border border-line bg-white/[0.04] hover:bg-gold/10 hover:border-gold/40 transition-colors flex flex-col items-center justify-center gap-2 cursor-pointer group"
          >
            <Sparkles className="w-4 h-4 text-gold/70 group-hover:text-gold" />
            <span className="text-[11px] tracking-wide text-white/80">{k}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- DETAIL GALLERY (31 uploaded images) ---------- */
function Gallery() {
  return (
    <section className="border-t border-line/60 bg-gradient-to-b from-[var(--gx-bg)] via-[#0B0B10] to-[var(--gx-bg)]">
      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-20 lg:py-28">
        <div className="max-w-[60ch]">
          <p className="eyebrow mb-4">In Detail</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.08]">
            Crafted, finished, considered &mdash; <span className="text-gold">in every detail.</span>
          </h2>
          <p className="lead mt-7 max-w-[58ch]">
            Every chamfer. Every backlit icon. Every hand-applied finish. Photographed
            close, lit honestly &mdash; with nothing to hide.
          </p>
        </div>

        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mt-14"
          data-testid="ds-gallery"
        >
          {DYNAMIC_SERIES_GALLERY.map((g, i) => (
            <figure
              key={i}
              className="group relative rounded-2xl overflow-hidden border border-line bg-[var(--gx-surface)] hover:border-gold/40 transition-colors"
              data-testid={`ds-gallery-item-${i}`}
            >
              <div className="aspect-[4/5] overflow-hidden">
                <img
                  src={g.src}
                  alt={g.caption}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
              </div>
              <figcaption className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/90 via-black/40 to-transparent">
                <div className="text-[10px] tracking-[0.32em] uppercase text-amber-100/80">
                  {(i + 1).toString().padStart(2, "0")} · Detail
                </div>
                <div className="font-display text-base text-white mt-1">{g.caption}</div>
                {g.note && <div className="text-[12px] text-dim mt-1 line-clamp-2">{g.note}</div>}
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- CTA ---------- */
function CTA({ onBook, waLink }) {
  return (
    <section className="relative border-t border-line/60 bg-[var(--gx-black)]">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(201,169,97,0.22),transparent_55%)]" />
      <div className="relative max-w-[1100px] mx-auto px-5 lg:px-10 py-20 lg:py-28 text-center">
        <p className="eyebrow mb-4">Experience it live</p>
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight max-w-[24ch] mx-auto leading-[1.08]">
          Some things must be felt to be understood.
        </h2>
        <p className="lead mt-7 max-w-[52ch] mx-auto">
          Book a private demo at our experience centre, or have a senior advisor walk you through it on a video call.
        </p>
        <div className="flex flex-wrap items-center justify-center gap-3 mt-10">
          <button onClick={onBook} data-testid="ds-page-cta" className="btn-gold">
            Book a Private Demo <ArrowRight className="w-4 h-4" />
          </button>
          <a href={waLink} target="_blank" rel="noopener noreferrer" className="btn-outline">
            <MessageCircle className="w-4 h-4" /> WhatsApp Us
          </a>
          <Link to="/solutions/complete-home" className="btn-outline">
            Explore Complete Home
          </Link>
        </div>
      </div>
    </section>
  );
}

function slugify(s) {
  return String(s).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}
